import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { FaTrash } from "react-icons/fa";
import EmailRichTextEditor from "./EmailRichTextEditor";
import Pagination from "./Pagination";
import ReportFilterPanel from "./ReportFilterPanel";
import StyledSelect from "./StyledSelect";
import "../styles/Communications.css";
import "../styles/Reports.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";
const DIRECTORY_PAGE_SIZE = 10;
const headers = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});
const EMPTY_FILTERS = {
  search: "",
  category: "",
  status: "",
  org: "",
  province: "",
  city: "",
  recruiter: "",
  position: "",
  secondaryPosition: "",
};
const splitEmails = (value) => [
  ...new Set(
    String(value || "")
      .split(/[;,\n]+/)
      .map((item) => item.trim().toLowerCase())
      .filter(Boolean),
  ),
];

function FilterSelect({ label, value, options = [], onChange }) {
  return (
    <div className="communications-filter-field">
      <span>{label}</span>
      <StyledSelect
        value={value}
        options={[{ value: "", label: "All" }, ...options]}
        onChange={onChange}
        minWidth={0}
        ariaLabel={`${label} filter`}
      />
    </div>
  );
}

function RecipientSortHeader({ label, column, sort, onSort }) {
  const active = sort.column === column;
  return (
    <th aria-sort={active ? (sort.direction === "asc" ? "ascending" : "descending") : "none"}>
      <button type="button" className="nw-sort-button" onClick={() => onSort(column)}>
        <span>{label}</span>
        <span className={`nw-sort-indicator ${active ? `is-${sort.direction}` : ""}`} aria-hidden="true">
          <i className="fa-solid fa-arrow-up" />
        </span>
      </button>
    </th>
  );
}

export default function EmailComposer({
  initialUser = null,
  showDirectory = false,
  onSent,
}) {
  const [bootstrap, setBootstrap] = useState({
    admin: null,
    groups: [],
    templates: [],
  });
  const [recipients, setRecipients] = useState(initialUser?.email || "");
  const [selectedUsers, setSelectedUsers] = useState(() =>
    initialUser?.id ? { [initialUser.id]: initialUser } : {},
  );
  const [fromAddresses, setFromAddresses] = useState("");
  const [replyTo, setReplyTo] = useState("");
  const [cc, setCc] = useState("");
  const [replyToGroup, setReplyToGroup] = useState(false);
  const [replyGroupId, setReplyGroupId] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [updateTemplate, setUpdateTemplate] = useState(false);
  const [templateTitle, setTemplateTitle] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [filterOptions, setFilterOptions] = useState({});
  const [directory, setDirectory] = useState([]);
  const [directoryLoading, setDirectoryLoading] = useState(false);
  const [directoryPage, setDirectoryPage] = useState(1);
  const [directorySort, setDirectorySort] = useState({ column: "name", direction: "asc" });
  const [sending, setSending] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    let active = true;
    const requests = [
      axios.get(`${API_BASE}/api/communications/bootstrap`, {
        headers: headers(),
      }),
    ];
    if (showDirectory)
      requests.push(
        axios.get(`${API_BASE}/api/communications/filters`, {
          headers: headers(),
        }),
      );
    Promise.all(requests)
      .then(([bootstrapResponse, filterResponse]) => {
        if (!active) return;
        setBootstrap(bootstrapResponse.data);
        const adminEmail = bootstrapResponse.data?.admin?.email || "";
        setFromAddresses((current) => current || adminEmail);
        setReplyTo((current) => current || adminEmail);
        if (filterResponse) setFilterOptions(filterResponse.data || {});
      })
      .catch((error) =>
        setFeedback({
          type: "error",
          text:
            error.response?.data?.message || "Unable to load email settings.",
        }),
      );
    return () => {
      active = false;
    };
  }, [showDirectory]);

  useEffect(() => {
    if (!initialUser?.email) return;
    setRecipients(initialUser.email);
    if (initialUser.id) setSelectedUsers({ [initialUser.id]: initialUser });
  }, [initialUser]);

  const searchRecipients = useCallback(
    async (nextFilters, signal) => {
      setDirectoryLoading(true);
      setFeedback(null);
      try {
        const { data } = await axios.get(
          `${API_BASE}/api/communications/recipients`,
          { params: nextFilters, headers: headers(), signal },
        );
        setDirectory(Array.isArray(data) ? data : []);
        setDirectoryPage(1);
      } catch (error) {
        if (error.code === "ERR_CANCELED") return;
        setFeedback({
          type: "error",
          text: error.response?.data?.message || "Unable to search recipients.",
        });
      } finally {
        if (!signal?.aborted) setDirectoryLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (!showDirectory) return undefined;
    const controller = new AbortController();
    const delay = filters.search.trim() ? 300 : 0;
    const timeout = window.setTimeout(
      () => searchRecipients(filters, controller.signal),
      delay,
    );
    return () => {
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [filters, showDirectory, searchRecipients]);

  const selectedEmailSet = useMemo(
    () => new Set(splitEmails(recipients)),
    [recipients],
  );
  const sortedDirectory = useMemo(() => [...directory].sort((left, right) => {
    const getters = {
      employeeId: (user) => user.employeeId || "",
      name: (user) => `${user.firstName || ""} ${user.lastName || ""}`.trim(),
      email: (user) => user.email || "",
      status: (user) => user.status || "",
      category: (user) => user.category || "",
      org: (user) => user.org || "",
      city: (user) => user.city || "",
    };
    const getter = getters[directorySort.column];
    const a = String(getter(left)).toLowerCase();
    const b = String(getter(right)).toLowerCase();
    return a.localeCompare(b, undefined, { numeric: true }) * (directorySort.direction === "asc" ? 1 : -1);
  }), [directory, directorySort]);
  const pagedDirectory = sortedDirectory.slice(
    (directoryPage - 1) * DIRECTORY_PAGE_SIZE,
    directoryPage * DIRECTORY_PAGE_SIZE,
  );
  const changeDirectorySort = (column) => {
    setDirectorySort((current) => ({
      column,
      direction: current.column === column && current.direction === "asc" ? "desc" : "asc",
    }));
    setDirectoryPage(1);
  };
  const toggleRecipient = (user) => {
    const email = String(user.email || "")
      .trim()
      .toLowerCase();
    if (!email) return;
    const next = new Set(selectedEmailSet);
    const nextUsers = { ...selectedUsers };
    if (next.has(email)) {
      next.delete(email);
      delete nextUsers[user.id];
    } else {
      next.add(email);
      nextUsers[user.id] = user;
    }
    setRecipients([...next].join(", "));
    setSelectedUsers(nextUsers);
  };

  const applyTemplate = (id) => {
    setTemplateId(id);
    if (!id) setUpdateTemplate(false);
    const template = bootstrap.templates.find(
      (item) => String(item.id) === String(id),
    );
    if (template) {
      setSubject(template.subject || "");
      setBody(template.body || "");
    }
  };

  const addFiles = (files) => {
    const incoming = [...files];
    setAttachments((current) => [...current, ...incoming].slice(0, 10));
  };

  const sendEmail = async () => {
    if (sending) return;
    if (saveAsTemplate && !templateTitle.trim()) {
      setFeedback({
        type: "error",
        text: "Template title is required when Save as Template is checked.",
      });
      return;
    }
    setSending(true);
    setFeedback(null);
    try {
      const recipientEmails = splitEmails(recipients);
      const selectedIds = Object.values(selectedUsers)
        .filter((user) =>
          recipientEmails.includes(String(user.email || "").toLowerCase()),
        )
        .map((user) => Number(user.id));
      const data = new FormData();
      data.append("recipients", JSON.stringify(recipientEmails));
      data.append("recipientUserIds", JSON.stringify(selectedIds));
      data.append("fromAddresses", JSON.stringify(splitEmails(fromAddresses)));
      data.append("replyTo", JSON.stringify(splitEmails(replyTo)));
      data.append("cc", JSON.stringify(splitEmails(cc)));
      data.append("replyGroupId", replyToGroup ? replyGroupId : "");
      data.append("subject", subject);
      data.append("body", body);
      data.append("saveAsTemplate", String(saveAsTemplate));
      data.append("templateTitle", templateTitle);
      data.append("updateTemplateId", updateTemplate ? templateId : "");
      attachments.forEach((file) => data.append("attachments", file));
      const response = await axios.post(
        `${API_BASE}/api/communications/send`,
        data,
        { headers: { ...headers(), "Content-Type": "multipart/form-data" } },
      );
      setFeedback({
        type: "success",
        text: response.data?.message || "Email sent successfully.",
      });
      if (response.data?.updatedTemplate) {
        setBootstrap((current) => ({
          ...current,
          templates: current.templates.map((template) =>
            String(template.id) === String(response.data.updatedTemplate.id)
              ? { ...template, ...response.data.updatedTemplate }
              : template,
          ),
        }));
      }
      setSubject("");
      setBody("");
      setCc("");
      setTemplateId("");
      setAttachments([]);
      setSaveAsTemplate(false);
      setUpdateTemplate(false);
      setTemplateTitle("");
      onSent?.();
    } catch (error) {
      setFeedback({
        type: "error",
        text: error.response?.data?.message || "The email could not be sent.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="email-composer">
      {showDirectory ? (
        <section className="recipient-directory">
          <div className="recipient-directory-heading">
            <span aria-hidden="true"><i className="fa fa-users" /></span>
            <div>
              <h3>Search/Select Email Recipients</h3>
              <p>Filter the directory and select the people who should receive this email.</p>
            </div>
          </div>
          <ReportFilterPanel>
          <div className="communications-filters">
            <div className="communications-filter-field communications-filter-search">
              <label htmlFor="compose-recipient-search">Search</label>
              <input
                id="compose-recipient-search"
                value={filters.search}
                placeholder="First name, last name, email or employee ID"
                onChange={(event) =>
                  setFilters((current) => ({
                    ...current,
                    search: event.target.value,
                  }))
                }
              />
            </div>
            <FilterSelect
              label="Category"
              value={filters.category}
              options={filterOptions.categories}
              onChange={(value) =>
                setFilters((c) => ({ ...c, category: value }))
              }
            />
            <FilterSelect
              label="Status"
              value={filters.status}
              options={filterOptions.statuses}
              onChange={(value) => setFilters((c) => ({ ...c, status: value }))}
            />
            <FilterSelect
              label="Org"
              value={filters.org}
              options={filterOptions.orgs}
              onChange={(value) => setFilters((c) => ({ ...c, org: value }))}
            />
            <FilterSelect
              label="Province"
              value={filters.province}
              options={filterOptions.provinces}
              onChange={(value) =>
                setFilters((c) => ({ ...c, province: value }))
              }
            />
            <FilterSelect
              label="City"
              value={filters.city}
              options={filterOptions.cities}
              onChange={(value) => setFilters((c) => ({ ...c, city: value }))}
            />
            <FilterSelect
              label="Recruiter"
              value={filters.recruiter}
              options={filterOptions.recruiters}
              onChange={(value) =>
                setFilters((c) => ({ ...c, recruiter: value }))
              }
            />
            <FilterSelect
              label="Position"
              value={filters.position}
              options={filterOptions.positions}
              onChange={(value) =>
                setFilters((c) => ({ ...c, position: value }))
              }
            />
            <FilterSelect
              label="Secondary Position"
              value={filters.secondaryPosition}
              options={filterOptions.secondaryPositions}
              onChange={(value) =>
                setFilters((c) => ({ ...c, secondaryPosition: value }))
              }
            />
          </div>
          </ReportFilterPanel>
          <div className="communications-table-actions">
            <button type="button" className="btn btn-secondary" onClick={() => {
              setFilters(EMPTY_FILTERS);
            }}>Clear Filter</button>
          </div>
          <div className="recipient-table-wrap nw-data-table-shell">
            <div className="communications-table-scroll">
            <table className="communications-table nw-data-table" data-no-enhance-table="true">
              <thead>
                <tr>
                  <th><span className="nw-static-header">Select</span></th>
                  <RecipientSortHeader label="Employee" column="employeeId" sort={directorySort} onSort={changeDirectorySort} />
                  <RecipientSortHeader label="Name" column="name" sort={directorySort} onSort={changeDirectorySort} />
                  <RecipientSortHeader label="Email" column="email" sort={directorySort} onSort={changeDirectorySort} />
                  <RecipientSortHeader label="Status" column="status" sort={directorySort} onSort={changeDirectorySort} />
                  <RecipientSortHeader label="Category" column="category" sort={directorySort} onSort={changeDirectorySort} />
                  <RecipientSortHeader label="Org" column="org" sort={directorySort} onSort={changeDirectorySort} />
                  <RecipientSortHeader label="City" column="city" sort={directorySort} onSort={changeDirectorySort} />
                </tr>
              </thead>
              <tbody>
                {directoryLoading ? (
                  <tr>
                    <td colSpan="8">Loading recipients...</td>
                  </tr>
                ) : (
                  pagedDirectory.map((user) => (
                    <tr key={user.id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedEmailSet.has(
                            String(user.email).toLowerCase(),
                          )}
                          onChange={() => toggleRecipient(user)}
                        />
                      </td>
                      <td>{user.employeeId || "-"}</td>
                      <td>
                        {`${user.firstName || ""} ${user.lastName || ""}`.trim()}
                      </td>
                      <td>{user.email}</td>
                      <td>{user.status || "-"}</td>
                      <td>{user.category || "-"}</td>
                      <td>{user.org || "-"}</td>
                      <td>{user.city || "-"}</td>
                    </tr>
                  ))
                )}
                {!directoryLoading && !directory.length ? (
                  <tr>
                    <td colSpan="8">No recipients found.</td>
                  </tr>
                ) : null}
              </tbody>
            </table>
            </div>
            {!directoryLoading ? (
              <Pagination currentPage={directoryPage} totalItems={sortedDirectory.length} itemsPerPage={DIRECTORY_PAGE_SIZE} onPageChange={setDirectoryPage} scrollToTopOnChange={false} />
            ) : null}
          </div>
        </section>
      ) : null}

      <section className="email-compose-card">
        <div className="email-compose-heading">
          <i className="fa fa-envelope" aria-hidden="true" />
          <h2>Email Settings</h2>
        </div>
        <div className="email-compose-body">
      <section className="email-settings-card">
        <div className="email-settings-grid">
          <label className="email-field email-recipient-field">
            <span>Recipient(s)</span>
            <textarea
              rows="5"
              value={recipients}
              onChange={(event) => setRecipients(event.target.value)}
              placeholder="Enter email addresses separated by commas"
            />
            <small>{splitEmails(recipients).length} recipient(s)</small>
          </label>
          <div className="email-settings-fields">
            <label className="email-field">
              <span>From</span>
              <input
                value={fromAddresses}
                onChange={(event) => setFromAddresses(event.target.value)}
                placeholder="Email addresses separated by commas"
              />
            </label>
            <label className="email-field">
              <span>Reply-To</span>
              <textarea
                rows="2"
                value={replyTo}
                onChange={(event) => setReplyTo(event.target.value)}
                placeholder="Email addresses separated by commas"
              />
            </label>
            <label className="email-checkbox">
              <input
                type="checkbox"
                checked={replyToGroup}
                onChange={(event) => {
                  setReplyToGroup(event.target.checked);
                  if (!event.target.checked) setReplyGroupId("");
                }}
              />
              <span>Reply To Group</span>
            </label>
            {replyToGroup ? (
              <div className="email-field">
                <span>Reply-To Group</span>
                <StyledSelect
                  value={replyGroupId}
                  onChange={setReplyGroupId}
                  minWidth={0}
                  ariaLabel="Reply-To Group"
                  options={[
                    { value: "", label: "Select a group" },
                    ...bootstrap.groups.map((group) => ({
                      value: group.id,
                      label: `${group.name}${group.email ? ` — ${group.email}` : ""}`,
                    })),
                  ]}
                />
              </div>
            ) : null}
            <label className="email-field">
              <span>CC Address(es)</span>
              <input
                value={cc}
                onChange={(event) => setCc(event.target.value)}
                placeholder="Email addresses separated by commas"
              />
            </label>
            <label className="email-field">
              <span>Subject</span>
              <input
                value={subject}
                onChange={(event) => setSubject(event.target.value)}
              />
            </label>
            <div className="email-field">
              <span>Email Template</span>
              <StyledSelect
                value={templateId}
                onChange={applyTemplate}
                minWidth={0}
                ariaLabel="Email Template"
                options={[
                  { value: "", label: "None" },
                  ...bootstrap.templates.map((template) => ({
                    value: template.id,
                    label: template.title,
                  })),
                ]}
              />
            </div>
            <label className="email-checkbox">
              <input
                type="checkbox"
                checked={saveAsTemplate}
                onChange={(event) => {
                  setSaveAsTemplate(event.target.checked);
                  if (event.target.checked) setUpdateTemplate(false);
                }}
              />
              <span>Save as Template</span>
            </label>
            <label className="email-checkbox">
              <input
                type="checkbox"
                checked={updateTemplate}
                disabled={!templateId}
                onChange={(event) => {
                  setUpdateTemplate(event.target.checked);
                  if (event.target.checked) {
                    setSaveAsTemplate(false);
                    setTemplateTitle("");
                  }
                }}
              />
              <span>Update Selected Template</span>
            </label>
            {saveAsTemplate ? (
              <label className="email-field">
                <span>Template Title</span>
                <input
                  value={templateTitle}
                  onChange={(event) => setTemplateTitle(event.target.value)}
                />
              </label>
            ) : null}
          </div>
        </div>
      </section>

      <section className="email-body-card">
        <label>Body</label>
        <EmailRichTextEditor value={body} onChange={setBody} />
      </section>
      <section
        className="email-attachments"
        onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => {
          event.preventDefault();
          addFiles(event.dataTransfer.files);
        }}
      >
        <div className="attachment-heading">
          <span>
            <i className="fa fa-paperclip" aria-hidden="true" />
            <strong>Attachments</strong>
          </span>
          <small>Up to 10 files, 15 MB each</small>
        </div>
        <label className="attachment-drop">
          <i className="fa fa-upload" aria-hidden="true" />
          <span>Drag and drop files here or browse</span>
          <input
            type="file"
            multiple
            onChange={(event) => addFiles(event.target.files)}
          />
        </label>
        {attachments.length ? (
          <ul>
            {attachments.map((file, index) => (
              <li key={`${file.name}-${index}`}>
                <span>{file.name}</span>
                <button
                  type="button"
                  className="attachment-remove-button"
                  aria-label={`Discard attachment ${file.name}`}
                  title="Discard attachment"
                  onClick={() =>
                    setAttachments((current) =>
                      current.filter((_, itemIndex) => itemIndex !== index),
                    )
                  }
                >
                  <FaTrash aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </section>
      {feedback ? (
        <div
          className={`communications-feedback ${feedback.type}`}
          role="status"
        >
          {feedback.text}
        </div>
      ) : null}
      <div className="email-compose-actions">
        <button
          type="button"
          className="btn btn-primary"
          onClick={sendEmail}
          disabled={sending}
        >
          {sending ? "Sending..." : "Send Email"}{" "}
          <i className="fa fa-paper-plane" aria-hidden="true" />
        </button>
      </div>
        </div>
      </section>
    </div>
  );
}
